import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workspaceApi } from '../api/workspaceApi';
import { showSuccessToast, showErrorToast } from '../lib/toast';
import { useAuth } from '../context/AuthContext';
import { FEATURE_INTEGRATION_BIGQUERY } from '../constants/capabilities';

export function isWorkspaceLocked(workspace, capabilities) {
  const bigqueryEnabled = capabilities?.features?.[FEATURE_INTEGRATION_BIGQUERY] !== false;
  return !bigqueryEnabled && (workspace.sources || []).some(source => source?.type === 'bigquery');
}

export function useWorkspaceCardActions(workspaces, setWorkspaces) {
  const navigate = useNavigate();
  const { user, capabilities } = useAuth();
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [exportingWorkspaceId, setExportingWorkspaceId] = useState(null);

  const canEditWorkspaceJson = Boolean(user?.is_editor || user?.is_superuser);

  const handleDeleteWorkspace = useCallback(
    async id => {
      try {
        await workspaceApi.deleteWorkspace(id);
        setWorkspaces(prev => prev.filter(ws => ws.id !== id));
        setSelectedWorkspace(prev => (prev?.id === id ? null : prev));
        showSuccessToast('Workspace removed.');
      } catch (err) {
        console.error('Failed to delete workspace:', err);
      }
    },
    [setWorkspaces]
  );

  const handleUpdateWorkspace = useCallback(
    async (id, updates) => {
      try {
        const updatedWs = await workspaceApi.updateWorkspace(id, updates);
        setWorkspaces(prev => prev.map(ws => (ws.id === id ? updatedWs : ws)));
        setSelectedWorkspace(prev => (prev && prev.id === id ? updatedWs : prev));
        showSuccessToast('Workspace updated.');
        return updatedWs;
      } catch (err) {
        console.error('Failed to update workspace:', err);
        throw err;
      }
    },
    [setWorkspaces]
  );

  const handleExportWorkspaceJson = useCallback(async workspaceId => {
    setExportingWorkspaceId(workspaceId);
    try {
      await workspaceApi.downloadWorkspaceExport(workspaceId);
      showSuccessToast('Workspace JSON downloaded.');
    } catch (err) {
      console.error('Failed to export workspace:', err);
      showErrorToast('Could not download workspace export.');
    } finally {
      setExportingWorkspaceId(null);
    }
  }, []);

  const handleOpenWorkspace = useCallback(id => {
    navigate(`/workspace/${id}`);
  }, [navigate]);

  const handlePreviewWorkspace = useCallback(
    workspaceId => {
      const workspace = workspaces.find(ws => ws.id === workspaceId);
      if (workspace) setSelectedWorkspace(workspace);
    },
    [workspaces]
  );

  const getWorkspaceLocked = useCallback(
    workspace => isWorkspaceLocked(workspace, capabilities),
    [capabilities]
  );

  return {
    selectedWorkspace,
    setSelectedWorkspace,
    exportingWorkspaceId,
    canEditWorkspaceJson,
    handleDeleteWorkspace,
    handleUpdateWorkspace,
    handleExportWorkspaceJson,
    handleOpenWorkspace,
    handlePreviewWorkspace,
    getWorkspaceLocked,
  };
}
